import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import pluralize from 'pluralize';

import { formatGigPrice, formatPrice, safeDateFormat } from 'utils/helpers';
import styles from 'styles/components/gigs/GigDetails.module.scss';

const MilestoneInfo = ({ gig, milestone, gigOwner }) => {
  const duration = milestone?.inventory_space?.event_attendance
    ? milestone.live_time_duration * milestone.variable_quantity
    : milestone.live_time_duration;

  const getPrice = () => {
    const baseAmount = gigOwner ? milestone.total_amount : milestone.amount;
    let amount = baseAmount;

    if (milestone.guaranteed_gains) {
      const multiplier = Math.min(
        milestone.guaranteed_gains_current / milestone.guaranteed_gains_max,
        1
      );

      amount = multiplier * baseAmount;
    }

    return formatPrice(amount);
  };

  return (
    <div className={clsx(styles.info, 'border-bottom')}>
      <div className={styles.row}>
        Platform: <span>{milestone.network.name}</span>
      </div>

      <div className={styles.row}>
        Type: <span>{milestone.inventory_space.name}</span>
      </div>

      {milestone.variable_quantity > 1 && !milestone?.inventory_space?.event_attendance && (
        <div className={styles.row}>
          Quantity: <span>{milestone.variable_quantity}</span>
        </div>
      )}

      {milestone.live_time_unit !== 'none' && (
        <div className={styles.row}>
          {milestone.guaranteed_gains ? 'Guaranteed Gains:' : 'Live time:'}{' '}
          <span>
            {duration} {pluralize(milestone.live_time_unit, duration)}
          </span>
        </div>
      )}

      {milestone.earliest_start_time && (
        <div className={styles.row}>
          {milestone?.inventory_space?.event_attendance ? 'Start at' : 'Earliest start'}:{' '}
          <span>
            {' '}
            {safeDateFormat(
              new Date(milestone.earliest_start_time),
              milestone?.inventory_space?.event_attendance ? 'PPppp' : 'MMM d, yyyy'
            )}
          </span>
        </div>
      )}

      {milestone.delivery_by && (
        <div className={styles.row}>
          Delivery by:
          <span> {safeDateFormat(new Date(milestone.delivery_by), 'MMM d, yyyy')}</span>
        </div>
      )}

      {milestone.recording_type === 'green_screen' && (
        <div className={styles.row}>
          Recording type:
          <span>Green Screen</span>
        </div>
      )}

      <div className={styles.row}>
        {milestone.guaranteed_gains ? 'Maximum Payment:' : 'Payment:'}
        {formatGigPrice(gig, gigOwner ? milestone.total_amount : milestone.amount)}
      </div>

      {milestone.guaranteed_gains && (
        <div className={styles.row}>
          Delivered Amount:
          <span>{getPrice()}</span>
        </div>
      )}

      {milestone.fans_total_spent > 0 && (
        <div className={styles.row}>
          Fans Total Spent:
          <span>{formatPrice(milestone.fans_total_spent)}</span>
        </div>
      )}

      {milestone?.inventory_space?.event_attendance && (
        <>
          {milestone.location && (
            <div className={styles.row}>
              Location:{' '}
              {milestone.google_location ? (
                <a
                  href={milestone.google_location}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#5b00db', textDecoration: 'underline', textUnderlineOffset: 2 }}
                >
                  {milestone.location}
                </a>
              ) : (
                <span>{milestone.location}</span>
              )}
            </div>
          )}

          <div className={styles.row}>
            Travel Included: <span>{milestone.travel_included ? 'Yes' : 'No'}</span>
          </div>

          <div className={styles.row}>
            Lodging Included: <span>{milestone.lodging_included ? 'Yes' : 'No'}</span>
          </div>

          <div className={styles.row}>
            Meals Included: <span>{milestone.meals_included ? 'Yes' : 'No'}</span>
          </div>
        </>
      )}
    </div>
  );
};

MilestoneInfo.propTypes = {
  gig: PropTypes.object.isRequired,
  milestone: PropTypes.object.isRequired,
  gigOwner: PropTypes.bool,
};

MilestoneInfo.defaultProps = {
  gigOwner: false,
};

export default MilestoneInfo;
